import React, { useEffect, useState } from 'react';
import { Routes, Route, NavLink, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useLanguage } from '../../lib/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Send, Loader2, CheckCircle2, MessageSquare, X, ShieldCheck, Users, Plus, BookmarkPlus, AlertTriangle } from 'lucide-react';
import AccountStatusBanner, { getStatusConfig } from '../../components/AccountStatusBanner';

const useStatusBadge = () => {
  const { t } = useLanguage();
  return (s) => {
    const m = {
      en_attente_validation: { v: 'secondary', l: t('transfer.pendingValidation') },
      en_cours_traitement: { v: 'default', l: t('transfer.inProcessing') },
      valide: { v: 'default', l: t('transfer.validated') },
      execute: { v: 'default', l: t('transfer.executed') },
      annule_client: { v: 'destructive', l: t('transfer.cancelledClient') },
      annule_admin: { v: 'destructive', l: t('transfer.cancelledAdmin') },
      bloque: { v: 'destructive', l: t('transfer.blocked') },
      suspendu: { v: 'destructive', l: t('transfer.suspended') },
      refuse: { v: 'destructive', l: t('transfer.refused') },
    };
    const x = m[s] || { v: 'secondary', l: s };
    return <Badge variant={x.v}>{x.l}</Badge>;
  };
};

/**
 * Modal de validation de virement (réutilisable)
 */
function ValidationDialog({ open, transferId, onClose, onSuccess }) {
  const { t } = useLanguage();
  const [state, setState] = useState(null);
  const [codeValue, setCodeValue] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !transferId) return;
    setLoading(true);
    setError('');
    setCodeValue('');
    (async () => {
      try {
        const { data } = await api.get(`/client/transfers/${transferId}/next-code`);
        setState(data);
        if (data.finished) {
          setTimeout(() => { onSuccess && onSuccess(); onClose(); }, 800);
        }
      } catch (e) {
        setError(e?.response?.data?.detail || t('transfer.loadError'));
      } finally {
        setLoading(false);
      }
    })();
  }, [open, transferId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!codeValue || !state?.next_code) return;
    setVerifying(true); setError('');
    await new Promise((r) => setTimeout(r, 5500));
    try {
      const { data } = await api.post('/client/transfers/submit-code', {
        transfer_id: transferId,
        numero_code: state.next_code.numero,
        code: codeValue,
      });
      if (!data.success) {
        setError(data.message || t('transfer.invalidCodeRetry'));
        setCodeValue('');
        setVerifying(false);
        return;
      }
      if (data.finished) {
        setState({ ...state, finished: true, progression: 100 });
        setVerifying(false);
        return;
      }
      setState({
        ...state,
        progression: data.progression,
        next_code: data.next_code,
      });
      setCodeValue('');
      setVerifying(false);
    } catch (e) {
      setError(e?.response?.data?.detail || t('transfer.genericError'));
      setVerifying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        {loading && (
          <div className="py-10 text-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2"/> {t('transfer.loadingShort')}
          </div>
        )}
        {!loading && state && !state.finished && state.next_code && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-blue-700"/> {t('transfer.validationTitle')}
              </DialogTitle>
              <DialogDescription>
                {t('transfer.validationDescPrefix')} <strong>{(state.montant || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</strong>{state.beneficiaire_nom ? <> {t('transfer.validationDescTo')} <strong>{state.beneficiaire_nom}</strong></> : null}{t('transfer.validationDescSuffix')}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="text-sm font-semibold text-blue-800 mb-2 uppercase tracking-wide">{state.next_code.libelle || t('transfer.codeValidationDefault')}</div>
              <Input className="mt-1 text-lg tracking-widest text-center font-mono" value={codeValue} disabled={verifying}
                onChange={(e) => { setCodeValue(e.target.value); setError(''); }}
                placeholder={state.next_code.libelle || t('transfer.enterReceivedCode')} data-testid="input-transfer-code" autoFocus/>
              {error && <div className="text-rose-600 text-sm mt-2 text-center">{error}</div>}
              <div className="mt-5">
                <Progress value={state.progression || 0} className="h-2 transfer-progress"/>
                <div className="text-xs text-slate-500 mt-1.5 text-center">{t('transfer.securingTransfer')} — {state.progression || 0}%</div>
              </div>
              <div className="mt-4 text-xs text-slate-500 bg-blue-50 border border-blue-100 rounded-lg p-3">
                💡 {t('transfer.tipReopenLater')}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={verifying} data-testid="btn-close-validation">
                <X className="w-4 h-4 mr-1"/> {t('transfer.close')}
              </Button>
              <Button onClick={submit} disabled={verifying || !codeValue} className="bg-blue-700" data-testid="btn-validate-code">
                {verifying ? <><Loader2 className="w-4 h-4 animate-spin mr-2"/> {t('transfer.verification')}</> : t('transfer.validate')}
              </Button>
            </DialogFooter>
          </>
        )}
        {!loading && state?.finished && (
          <div className="py-6 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-3">
              <CheckCircle2 className="w-7 h-7"/>
            </div>
            <h2 className="text-xl font-bold">{t('transfer.transferValidatedOk')}</h2>
            <p className="text-slate-600 text-sm mt-2">{t('transfer.transferProcessing')}</p>
            <Button className="mt-5 bg-blue-700" onClick={() => { onSuccess && onSuccess(); onClose(); }} data-testid="btn-transfer-success-close">
              {t('transfer.close')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function NewTransfer({ onCreated }) {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    type_virement: 'sepa',
    devise: 'EUR',
    pays_beneficiaire: 'France',
    adresse_beneficiaire: '',
    frais: 'SHA',
    beneficiaire_nom: '', beneficiaire_iban: '', beneficiaire_bic: '',
    montant: '', motif: '', date_execution: '',
  });
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTransferId, setActiveTransferId] = useState(null);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [pickedBeneficiary, setPickedBeneficiary] = useState('');
  const [saveAsBeneficiary, setSaveAsBeneficiary] = useState(false);
  const [saveLabel, setSaveLabel] = useState('');
  const [pendingTransfer, setPendingTransfer] = useState(null);

  // Statuses considered "pending" (must match backend)
  const PENDING_STATUSES = ['en_attente_validation', 'en_cours_traitement', 'valide', 'bloque', 'suspendu'];

  const refreshPending = async () => {
    try {
      const { data } = await api.get('/client/transfers');
      const items = Array.isArray(data) ? data : (data?.items || []);
      const found = items.find((x) => PENDING_STATUSES.includes(x.statut));
      setPendingTransfer(found || null);
    } catch { /* silent */ }
  };

  useEffect(() => { (async () => {
    try {
      const r = await api.get('/client/me');
      setMe(r.data.user);
    } catch { /* silent */ }
    try {
      const b = await api.get('/client/beneficiaries');
      setBeneficiaries(Array.isArray(b.data) ? b.data : (b.data?.items || []));
    } catch { /* silent */ }
    try {
      const raw = sessionStorage.getItem('bfi_beneficiary_prefill');
      if (raw) {
        const p = JSON.parse(raw);
        setForm((f) => ({ ...f, ...p }));
        sessionStorage.removeItem('bfi_beneficiary_prefill');
      }
    } catch { /* noop */ }
    await refreshPending();
  })(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const upd = (k, v) => setForm({ ...form, [k]: v });

  const pickBeneficiary = (id) => {
    setPickedBeneficiary(id);
    if (!id || id === 'none') return;
    const b = beneficiaries.find((x) => x.id === id);
    if (b) {
      setForm((f) => ({
        ...f,
        beneficiaire_nom: b.name || '',
        beneficiaire_iban: b.iban || '',
        beneficiaire_bic: b.bic || '',
      }));
    }
  };

  const persistBeneficiary = async () => {
    if (!saveAsBeneficiary) return;
    if (!form.beneficiaire_nom || !form.beneficiaire_iban) return;
    try {
      await api.post('/client/beneficiaries', {
        label: saveLabel || form.beneficiaire_nom,
        name: form.beneficiaire_nom,
        iban: form.beneficiaire_iban.replace(/\s+/g, '').toUpperCase(),
        bic: (form.beneficiaire_bic || '').replace(/\s+/g, '').toUpperCase() || null,
      });
      toast.success(t('transfer.savedBeneficiaryOk'));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Beneficiary save failed', e?.response?.data?.detail);
    }
  };
  const isSwift = form.type_virement === 'swift';
  const cfg = getStatusConfig(me?.statut_compte || 'actif');
  const blocked = !cfg.canTransfer;

  const init = async () => {
    if (blocked) { toast.error(t('transfer.blockedAccount')); return; }
    if (pendingTransfer) { toast.error(t('transfer.pendingWarning')); return; }
    if (!form.beneficiaire_nom || !form.beneficiaire_iban || !form.montant || !form.motif) { toast.error(t('transfer.fillAllRequired')); return; }
    if (isSwift && !form.beneficiaire_bic) { toast.error(t('transfer.swiftBicRequired')); return; }
    if (parseFloat(form.montant) <= 0) { toast.error(t('transfer.invalidAmountErr')); return; }
    if (parseFloat(form.montant) > (me?.solde || 0)) { toast.error(t('transfer.insufficientBalance')); return; }
    setLoading(true);
    try {
      await persistBeneficiary();
      const { data } = await api.post('/client/transfers', { ...form, montant: parseFloat(form.montant) });
      setActiveTransferId(data.transfer_id);
      await refreshPending();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('transfer.genericError'));
      // Backend may have just registered a pending state — refresh to surface the banner.
      await refreshPending();
    }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-5">
      <AccountStatusBanner statut={me?.statut_compte}/>

      {/* Pending transfer warning (Feature v4) */}
      {pendingTransfer && (
        <div
          className="rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-4 flex items-start gap-3"
          data-testid="banner-pending-transfer"
        >
          <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0"/>
          <div className="flex-1">
            <div className="font-semibold text-amber-800 dark:text-amber-200">{t('transfer.pendingExistsTitle')}</div>
            <div className="text-sm text-amber-700 dark:text-amber-300 mt-1">
              {t('transfer.pendingWarning')}
            </div>
            <div className="mt-2">
              <Link
                to="/dashboard/virement/en-cours"
                className="text-sm font-medium text-amber-800 dark:text-amber-200 underline hover:no-underline"
                data-testid="link-go-pending"
              >
                {t('transfer.goToPending')} →
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className={`bg-white border border-slate-200 rounded-2xl p-6 ${blocked || pendingTransfer ? 'opacity-60 pointer-events-none' : ''}`}>
        <h2 className="font-semibold text-lg">{t('transfer.newTransfer')}</h2>
        <p className="text-slate-500 text-sm">{t('transfer.availableBalance')} : <span className="font-semibold text-slate-900">{(me?.solde || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span></p>

        {/* Type SEPA / SWIFT */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <button onClick={() => upd('type_virement', 'sepa')} className={`p-4 rounded-xl border text-left transition ${form.type_virement === 'sepa' ? 'border-blue-700 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`} data-testid="btn-type-sepa">
            <div className="font-semibold text-sm">🇪🇺 {t('transfer.sepa')}</div>
            <div className="text-xs text-slate-500 mt-1">{t('transfer.sepaDesc')}</div>
          </button>
          <button onClick={() => upd('type_virement', 'swift')} className={`p-4 rounded-xl border text-left transition ${form.type_virement === 'swift' ? 'border-blue-700 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`} data-testid="btn-type-swift">
            <div className="font-semibold text-sm">🌍 {t('transfer.swift')}</div>
            <div className="text-xs text-slate-500 mt-1">{t('transfer.swiftDesc')}</div>
          </button>
        </div>

        {/* Bénéficiaires sauvegardés */}
        <div className="mt-5 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <Label className="flex items-center gap-1"><Users className="w-3.5 h-3.5"/> {t('transfer.savedBeneficiary')}</Label>
            {beneficiaries.length === 0 ? (
              <div className="mt-1 text-xs text-slate-500">
                {t('transfer.noSavedBeneficiary')} <Link to="/dashboard/beneficiaires" className="text-blue-700 hover:underline">{t('transfer.manageBeneficiaries')}</Link>
              </div>
            ) : (
              <Select value={pickedBeneficiary || 'none'} onValueChange={pickBeneficiary}>
                <SelectTrigger data-testid="select-saved-beneficiary"><SelectValue placeholder={t('transfer.pickBeneficiary')}/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('transfer.manualEntry')}</SelectItem>
                  {beneficiaries.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.label} ({b.name})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <Link to="/dashboard/beneficiaires" className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1 self-end pb-2">
            <Plus className="w-3 h-3"/> {t('transfer.manage')}
          </Link>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mt-3">
          <div><Label>{t('transfer.recipientFullName')}</Label><Input value={form.beneficiaire_nom} onChange={(e) => upd('beneficiaire_nom', e.target.value)} data-testid="input-beneficiaire_nom"/></div>
          <div><Label>{t('transfer.ibanOrAccount')}</Label><Input value={form.beneficiaire_iban} onChange={(e) => upd('beneficiaire_iban', e.target.value.toUpperCase())} placeholder={isSwift ? t('transfer.ibanOrAccount') : 'FR76...'} data-testid="input-beneficiaire_iban"/></div>
          <div><Label>{isSwift ? t('transfer.bicSwiftReq') : t('transfer.bicSwiftOpt')}</Label><Input value={form.beneficiaire_bic} onChange={(e) => upd('beneficiaire_bic', e.target.value.toUpperCase())} placeholder="ex: BNPAFRPP" data-testid="input-beneficiaire_bic"/></div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2"><Label>{t('transfer.amountLabel')}</Label><Input type="number" value={form.montant} onChange={(e) => upd('montant', e.target.value)} step="0.01" data-testid="input-montant"/></div>
            <div><Label>{t('transfer.currencyLabel')}</Label>
              <select value={form.devise} onChange={(e) => upd('devise', e.target.value)} disabled={!isSwift} className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm bg-white disabled:bg-slate-100" data-testid="select-devise">
                <option value="EUR">EUR</option><option value="USD">USD</option><option value="GBP">GBP</option><option value="CHF">CHF</option><option value="CAD">CAD</option><option value="JPY">JPY</option>
              </select>
            </div>
          </div>
          {isSwift && (<>
            <div><Label>{t('transfer.beneficiaryCountry')}</Label><Input value={form.pays_beneficiaire} onChange={(e) => upd('pays_beneficiaire', e.target.value)} placeholder="ex: États-Unis" data-testid="input-pays"/></div>
            <div><Label>{t('transfer.beneficiaryAddress')}</Label><Input value={form.adresse_beneficiaire} onChange={(e) => upd('adresse_beneficiaire', e.target.value)} placeholder="123 Main St, NYC" data-testid="input-adresse-benef"/></div>
            <div className="md:col-span-2"><Label>{t('transfer.bankFees')}</Label>
              <select value={form.frais} onChange={(e) => upd('frais', e.target.value)} className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="select-frais">
                <option value="SHA">{t('transfer.feesShaShort')}</option>
                <option value="OUR">{t('transfer.feesOurShort')}</option>
                <option value="BEN">{t('transfer.feesBenShort')}</option>
              </select>
            </div>
          </>)}
          <div className="md:col-span-2"><Label>{t('transfer.reasonLabel')}</Label><Textarea value={form.motif} onChange={(e) => upd('motif', e.target.value)} rows={2} data-testid="input-motif"/></div>
          <div><Label>{t('transfer.executionDate')}</Label><Input type="date" value={form.date_execution} onChange={(e) => upd('date_execution', e.target.value)} data-testid="input-date_execution"/></div>
        </div>
        {/* Save as beneficiary */}
        {pickedBeneficiary === '' || pickedBeneficiary === 'none' ? (
          <div className="mt-5 p-3 rounded-xl bg-blue-50/60 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={saveAsBeneficiary} onChange={(e) => setSaveAsBeneficiary(e.target.checked)} data-testid="check-save-beneficiary" />
              <BookmarkPlus className="w-4 h-4 text-blue-700"/> {t('transfer.saveAsBeneficiary')}
            </label>
            {saveAsBeneficiary && (
              <div className="mt-2">
                <Input placeholder={t('transfer.saveLabelPlaceholder')} value={saveLabel} onChange={(e) => setSaveLabel(e.target.value)} data-testid="input-save-label"/>
              </div>
            )}
          </div>
        ) : null}
        <div className="mt-5 flex justify-end">
          <Button onClick={init} disabled={loading || !!pendingTransfer} className="bg-blue-700" data-testid="btn-init-transfer">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <Send className="w-4 h-4 mr-2"/>}
            {isSwift ? t('transfer.initSwift') : t('transfer.initSepa')}
          </Button>
        </div>
      </div>

      <ValidationDialog
        open={!!activeTransferId}
        transferId={activeTransferId}
        onClose={() => { setActiveTransferId(null); refreshPending(); }}
        onSuccess={() => {
          setForm({ type_virement: 'sepa', devise: 'EUR', pays_beneficiaire: 'France', adresse_beneficiaire: '', frais: 'SHA', beneficiaire_nom: '', beneficiaire_iban: '', beneficiaire_bic: '', montant: '', motif: '', date_execution: '' });
          setPickedBeneficiary('');
          setSaveAsBeneficiary(false);
          setSaveLabel('');
          refreshPending();
          onCreated && onCreated();
        }}
      />
    </div>
  );
}

function TransferList({ status, title, allowCancel = false, allowResume = false }) {
  const { t } = useLanguage();
  const statusBadge = useStatusBadge();
  const [items, setItems] = useState([]);
  const [msg, setMsg] = useState(null);
  const [resumeId, setResumeId] = useState(null);
  const load = async () => {
    const r = await api.get('/client/transfers', { params: status ? { status } : {} });
    setItems(r.data);
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line
  const cancel = async (id) => {
    if (!window.confirm(t('transfer.confirmCancel'))) return;
    try { await api.post(`/client/transfers/${id}/cancel`); toast.success(t('transfer.transferCancelled')); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('transfer.genericError')); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b"><h2 className="font-semibold">{title}</h2></div>
      <div className="divide-y">
        {items.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">{t('transfer.noTransfers')}</div>}
        {items.map((tr) => (
          <div key={tr.id} className="p-4 flex flex-wrap items-center gap-3 justify-between">
            <div>
              <div className="font-medium">{tr.beneficiaire_nom}</div>
              <div className="text-xs text-slate-500">{tr.beneficiaire_iban} • {new Date(tr.created_at).toLocaleString()}</div>
              <div className="text-xs text-slate-500">{t('transfer.reasonShort')} : {tr.motif}</div>
            </div>
            <div className="text-right">
              <div className="font-bold">{tr.montant.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div>
              <div className="mt-1">{statusBadge(tr.statut)}</div>
            </div>
            <div className="flex flex-wrap gap-2">
              {tr.message_admin && <Button size="sm" variant="outline" onClick={() => setMsg(tr.message_admin)}><MessageSquare className="w-4 h-4"/></Button>}
              {allowResume && tr.statut === 'en_attente_validation' && (
                <Button size="sm" className="bg-blue-700" onClick={() => setResumeId(tr.id)} data-testid={`btn-resume-${tr.id}`}>
                  <ShieldCheck className="w-4 h-4 mr-1"/> {t('transfer.resumeValidate')}
                </Button>
              )}
              {allowCancel && tr.statut === 'en_attente_validation' && <Button size="sm" variant="destructive" onClick={() => cancel(tr.id)}>{t('transfer.cancelLabel')}</Button>}
            </div>
          </div>
        ))}
      </div>

      <ValidationDialog
        open={!!resumeId}
        transferId={resumeId}
        onClose={() => { setResumeId(null); load(); }}
        onSuccess={() => { setResumeId(null); load(); }}
      />

      <Dialog open={!!msg} onOpenChange={(o) => !o && setMsg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('transfer.advisorMessage')}</DialogTitle></DialogHeader>
          <p className="text-slate-700">{msg}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Virement() {
  const { t } = useLanguage();
  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-bold">{t('transfer.navTitle')}</h1>
      <div className="flex gap-2 overflow-x-auto">
        <NavLink end to="/dashboard/virement" className={({isActive}) => `px-4 py-2 rounded-full text-sm font-medium ${isActive ? 'bg-blue-700 text-white' : 'bg-white border border-slate-200'}`} data-testid="tab-new-transfer">{t('transfer.tabs.new')}</NavLink>
        <NavLink to="/dashboard/virement/en-cours" className={({isActive}) => `px-4 py-2 rounded-full text-sm font-medium ${isActive ? 'bg-blue-700 text-white' : 'bg-white border border-slate-200'}`} data-testid="tab-pending-transfer">{t('transfer.tabs.pending')}</NavLink>
        <NavLink to="/dashboard/virement/historique" className={({isActive}) => `px-4 py-2 rounded-full text-sm font-medium ${isActive ? 'bg-blue-700 text-white' : 'bg-white border border-slate-200'}`} data-testid="tab-history-transfer">{t('transfer.tabs.history')}</NavLink>
      </div>
      <Routes>
        <Route index element={<NewTransfer />} />
        <Route path="en-cours" element={<TransferList status="en_attente_validation" title={t('transfer.pendingTransfers')} allowCancel={true} allowResume={true} />} />
        <Route path="historique" element={<TransferList title={t('transfer.historyTransfers')} />} />
      </Routes>
    </div>
  );
}
